import classNames from "classnames";
import throttle from "lodash.throttle";
import React, { ChangeEvent, Component, createRef, FocusEvent } from "react";
import { getElementId } from "../../hooks";
import { CSS_CLASSES as BUTTON_CSS_CLASSES } from "../button/button-css-classes";
import { FormField } from "../form-field";
import { IconFormClearField } from "../icons";
import { ResizeObserver } from "../resize-observer/resize-observer";
import { AriaProps } from "../types";
import { CSS_CLASSES } from "./input-css-classes";
import { InputEnhancer } from "./input-enhancer";
import { InputElementRendererProps, InputOwnProps, InputProps, InputState } from "./input.types";

export const CLEAR_BUTTON_LABEL = "clear";

/**
 * The Input component represents a form field meant to capture text based
 * user input, usually as part of a form UI, but usable independently too.
 * Any place where we need to capture textual user input
 * (i.e. types via a keyboard) SHOULD use this component.
 *
 * It MUST allow the user of this component to render the component with
 * various state specific styles defined in the Blueprint Design System.
 *
 * This component is implemented to be used as a controlled form component.
 */
export class Input extends Component<InputProps, InputState> {
  static displayName = "Input";
  static defaultProps: Partial<InputOwnProps> = {
    startEnhancer: null,
    endEnhancer: null,
    showClearButton: false,
    alwaysShowClearButton: false,
    textAlign: "left",
    formField: false,
    rootProps: {},
    tags: null,
    color: "interactive",
    size: "medium",
  };

  state: InputState = {
    focused: false,
    layoutMeasurement:
      this.props.showClearButton ||
      this.props.alwaysShowClearButton ||
      this.props.startEnhancer !== null ||
      this.props.endEnhancer !== null ||
      this.props.tags !== null
        ? "required"
        : "not-required",
    initialPaddingTop: 0,
    initialPaddingRight: 0,
    initialPaddingBottom: 0,
    initialPaddingLeft: 0,
    paddingTopOffset: 0,
    paddingRightOffset: 0,
    paddingLeftOffset: 0,
    tagsLeft: 0,
    tagsRight: 0,
  };

  inputId = getElementId(this.props.id, "input");

  localInputRef = this.props.inputRef ?? createRef<HTMLInputElement>();

  inputElement: HTMLInputElement = null;

  localInputCallbackRef = (inputElement: HTMLInputElement) => {
    this.inputElement = inputElement;
    if (typeof this.localInputRef === "function") {
      this.localInputRef(inputElement);
    }
  };

  startEnhancerRef = createRef<HTMLSpanElement>();
  endEnhancerClusterRef = createRef<HTMLSpanElement>();
  tagsRef = createRef<HTMLSpanElement>();

  handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (!this.state.focused) {
      this.setState({ focused: true });
    }
    this.props.onFocus?.(event);
  };

  handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (this.state.focused) {
      this.setState({ focused: false });
    }
    this.props.onBlur?.(event);
  };

  handleClearButtonClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
    this.props.onChange?.({ target: { value: "" } } as ChangeEvent<HTMLInputElement>);
    event.stopPropagation();
    if (this.localInputRef) {
      if (typeof this.localInputRef === "function") {
        this.inputElement.focus();
      } else {
        this.localInputRef.current.focus();
      }
    }
    this.props.onClearButtonClick?.(event);
  };

  componentDidMount = () => {
    if (this.state.layoutMeasurement === "required") {
      this.updateLayoutMeasurementsWithInitialPaddingValues();
    }
  };

  componentDidUpdate = (prevProps: InputProps) => {
    if (
      this.props.tags !== prevProps.tags ||
      this.props.startEnhancer !== prevProps.startEnhancer ||
      this.props.endEnhancer !== prevProps.endEnhancer ||
      this.props.showClearButton !== prevProps.showClearButton ||
      this.props.alwaysShowClearButton !== prevProps.alwaysShowClearButton
    ) {
      if (this.state.layoutMeasurement === "not-required") {
        this.updateLayoutMeasurementsWithInitialPaddingValues();
      } else {
        this.updateLayoutMeasurements();
      }
    }
  };

  updateLayoutMeasurementsWithInitialPaddingValues = () => {
    const inputElement = typeof this.localInputRef === "function" ? this.inputElement : this.localInputRef.current;
    if (inputElement) {
      const { paddingTop, paddingRight, paddingBottom, paddingLeft } = window.getComputedStyle(inputElement);
      this.updateLayoutMeasurements({
        initialPaddingTop: parseInt(paddingTop, 10),
        initialPaddingRight: parseInt(paddingRight, 10),
        initialPaddingBottom: parseInt(paddingBottom, 10),
        initialPaddingLeft: parseInt(paddingLeft, 10),
      });
    }
  };

  updateLayoutMeasurements = (
    initialPaddingValues?: Pick<
      InputState,
      "initialPaddingTop" | "initialPaddingRight" | "initialPaddingBottom" | "initialPaddingLeft"
    >
  ) => {
    let paddingLeftOffset = 0;
    const maybeStartEnhancerWidth = (this.props.startEnhancer && this.startEnhancerRef.current.offsetWidth) || 0;
    paddingLeftOffset = maybeStartEnhancerWidth;

    let paddingRightOffset = 0;
    const maybeEndEnhancerClusterWidth =
      ((this.props.endEnhancer || this.props.showClearButton || this.props.alwaysShowClearButton) &&
        this.endEnhancerClusterRef.current.offsetWidth) ||
      0;
    paddingRightOffset = maybeEndEnhancerClusterWidth;

    let paddingTopOffset = 0;
    let tagsLeft = 0;
    let tagsRight = 0;
    const inputElement = typeof this.localInputRef === "function" ? this.inputElement : this.localInputRef.current;
    if (this.props.tags && inputElement) {
      tagsLeft = maybeStartEnhancerWidth;
      tagsRight = maybeEndEnhancerClusterWidth;

      const inputElementWidth = inputElement.offsetWidth;
      const tagsMaxWidth = inputElementWidth - tagsLeft - tagsRight;
      const tagsChildWidth = (this.tagsRef.current.firstElementChild as HTMLElement)?.offsetWidth || 0;
      if (tagsChildWidth < 0.75 * tagsMaxWidth) {
        paddingLeftOffset = paddingLeftOffset + tagsChildWidth;
      } else {
        paddingTopOffset = (this.props.tags && this.tagsRef.current.offsetHeight) || 0;
      }
    }

    /**
     * @todo: [BDS-503] use something like `react-fast-compare` to compare new state to current state before updating
     * This is useful for situations like the initial render when the <ResizeObserver> also call this function
     * right after first render, but result in identical state as calculated by in the call from componentDidMount
     */
    this.setState({
      layoutMeasurement: "ready",
      ...initialPaddingValues,
      paddingLeftOffset,
      paddingRightOffset,
      paddingTopOffset,
      tagsLeft,
      tagsRight,
    });
  };

  handleResize = throttle(() => {
    this.updateLayoutMeasurements();
  });

  render() {
    const {
      /* Own props */
      size,
      textAlign,
      startEnhancer,
      startEnhancerClassName,
      endEnhancer,
      endEnhancerClassName,
      showClearButton,
      alwaysShowClearButton,
      onClearButtonClick,
      formField,
      formFieldClassName,
      className,
      inputClassName,
      inputBorderClassName,
      inputRef,
      inputElementRenderer,
      rootProps,
      tags,
      color,

      /* FormField pass through props */
      formFieldMessageId,
      formFieldType,
      label,
      labelInfo,
      hideLabel,
      labelProps,
      secondaryAction,
      hint,
      success,
      error,
      textLength,

      /* Rest props */
      ...restProps
    } = this.props;

    const rootClassName = classNames(CSS_CLASSES.INPUT_ROOT, className);

    const maybeStartEnhancer = startEnhancer ? (
      <ResizeObserver onResize={this.handleResize}>
        <span
          ref={this.startEnhancerRef}
          className={classNames(CSS_CLASSES.INPUT_ENHANCER_WRAPPER, CSS_CLASSES.INPUT_START_ENHANCER_WRAPPER)}
        >
          <span
            className={classNames(
              CSS_CLASSES.INPUT_ENHANCER,
              {
                [CSS_CLASSES.INPUT_SMALL_START_ENHANCER]: size === "small",
                [CSS_CLASSES.INPUT_MEDIUM_START_ENHANCER]: size === "medium",
                [CSS_CLASSES.INPUT_LARGE_START_ENHANCER]: size === "large",
              },
              startEnhancerClassName
            )}
          >
            <InputEnhancer
              disabled={this.props.disabled}
              focused={this.state.focused}
              success={!!success}
              error={!!error}
              color={color}
              elementOrRenderer={startEnhancer}
            />
          </span>
        </span>
      </ResizeObserver>
    ) : null;

    const maybeClearButton =
      alwaysShowClearButton || (showClearButton && this.props.value) ? (
        <button
          disabled={this.props.disabled}
          title={CLEAR_BUTTON_LABEL}
          aria-label={CLEAR_BUTTON_LABEL}
          onClick={this.handleClearButtonClick}
          tabIndex={-1}
          aria-hidden="true"
          className={classNames(
            BUTTON_CSS_CLASSES.BUTTON_LITE_ROOT,
            BUTTON_CSS_CLASSES.BUTTON_GHOST,
            BUTTON_CSS_CLASSES.BUTTON_SMALL,
            CSS_CLASSES.INPUT_CLEAR_BUTTON,
            {
              [BUTTON_CSS_CLASSES.BUTTON_INTERACTIVE]: !error && color === "interactive",
              [BUTTON_CSS_CLASSES.BUTTON_PRIMARY]: !error && color === "primary",
              [BUTTON_CSS_CLASSES.BUTTON_ERROR]: error,
              [BUTTON_CSS_CLASSES.BUTTON_DISABLED]: this.props.disabled,
              [CSS_CLASSES.INPUT_SMALL_CLEAR_BUTTON]: size === "small",
              [CSS_CLASSES.INPUT_CLEAR_BUTTON_WITH_END_ENHANCER]: endEnhancer,
            }
          )}
        >
          <IconFormClearField size={24} color={error ? "error" : undefined} />
        </button>
      ) : null;

    const maybeEndEnhancer = endEnhancer ? (
      <InputEnhancer
        disabled={this.props.disabled}
        focused={this.state.focused}
        success={!!success}
        error={!!error}
        color={color}
        elementOrRenderer={endEnhancer}
      />
    ) : null;

    const maybeEndEnhancerCluster =
      showClearButton || alwaysShowClearButton || maybeEndEnhancer ? (
        <ResizeObserver onResize={this.handleResize}>
          <span
            ref={this.endEnhancerClusterRef}
            className={classNames(CSS_CLASSES.INPUT_ENHANCER_WRAPPER, CSS_CLASSES.INPUT_END_ENHANCER_WRAPPER)}
          >
            {(maybeClearButton || maybeEndEnhancer) && (
              <span
                className={classNames(
                  CSS_CLASSES.INPUT_ENHANCER,
                  {
                    [CSS_CLASSES.INPUT_SMALL_END_ENHANCER]: size === "small",
                    [CSS_CLASSES.INPUT_MEDIUM_END_ENHANCER]: size === "medium",
                    [CSS_CLASSES.INPUT_LARGE_END_ENHANCER]: size === "large",
                  },
                  endEnhancerClassName
                )}
              >
                {maybeClearButton}
                {maybeEndEnhancer}
              </span>
            )}
          </span>
        </ResizeObserver>
      ) : null;

    const maybeTags = tags ? (
      <ResizeObserver onResize={this.handleResize}>
        <span
          ref={this.tagsRef}
          className={CSS_CLASSES.INPUT_TAGS}
          style={
            this.state.layoutMeasurement === "ready"
              ? {
                  left: this.state.tagsLeft,
                  right: this.state.tagsRight,
                }
              : undefined
          }
        >
          {tags}
        </span>
      </ResizeObserver>
    ) : null;

    const getInputElement = (ariaProps: AriaProps) => {
      const inputElementProps: InputElementRendererProps = {
        ...ariaProps,
        ...restProps,
        id: this.inputId,
        onFocus: this.handleFocus,
        onBlur: this.handleBlur,
        ref: typeof this.localInputRef === "function" ? this.localInputCallbackRef : this.localInputRef,
        className: classNames(
          CSS_CLASSES.INPUT_ELEMENT,
          {
            [CSS_CLASSES.INPUT_SMALL]: size === "small",
            [CSS_CLASSES.INPUT_MEDIUM]: size === "medium",
            [CSS_CLASSES.INPUT_LARGE]: size === "large",
            [CSS_CLASSES.INPUT_PRIMARY]: color === "primary",
            [CSS_CLASSES.INPUT_ERROR]: !!error,
            [CSS_CLASSES.INPUT_SUCCESS]: !!success,
            [CSS_CLASSES.INPUT_DISABLED]: !!this.props.disabled,
            "text-left": textAlign === "left",
            "text-center": textAlign === "center",
            "text-right": textAlign === "right",
          },
          inputClassName,
          inputBorderClassName
        ),
        style:
          this.state.layoutMeasurement === "ready"
            ? {
                paddingTop: this.state.initialPaddingTop + this.state.paddingTopOffset,
                paddingRight: this.state.initialPaddingRight + this.state.paddingRightOffset,
                paddingBottom: this.state.initialPaddingBottom,
                paddingLeft: this.state.initialPaddingLeft + this.state.paddingLeftOffset,
              }
            : undefined,
      };

      return (
        <div className={rootClassName} {...rootProps}>
          {typeof inputElementRenderer === "function" ? (
            inputElementRenderer(inputElementProps)
          ) : (
            <input {...inputElementProps} />
          )}
          {maybeStartEnhancer}
          {maybeTags}
          {maybeEndEnhancerCluster}
        </div>
      );
    };

    return formField ? (
      <FormField
        formFieldId={this.inputId}
        formFieldMessageId={formFieldMessageId}
        formFieldType={formFieldType}
        label={label}
        labelInfo={labelInfo}
        hideLabel={hideLabel}
        labelProps={labelProps}
        secondaryAction={secondaryAction}
        hint={hint}
        success={success}
        error={error}
        textLength={textLength}
        focused={this.state.focused}
        required={this.props.required}
        disabled={this.props.disabled}
        readOnly={this.props.readOnly}
        className={formFieldClassName}
      >
        {getInputElement}
      </FormField>
    ) : (
      getInputElement({
        "aria-label": label,
        "aria-required": this.props.required,
        "aria-disabled": this.props.disabled,
        "aria-readonly": this.props.readOnly,
      })
    );
  }
}
